// Composant MetierCard - Carte sélectionnable pour un métier (V3 multi-sélection)
// Layout horizontal inspiré des listes shadcn

import { type ButtonHTMLAttributes } from 'react'
import { cn } from '../../lib/utils'

export interface MetierCardProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'onSelect'> {
  code: string
  nom: string
  description?: string
  selected?: boolean
  /** Ordre de sélection (1, 2, 3) - utilisé en mode multi-sélection */
  selectionOrder?: number
  /** Indique si la sélection max est atteinte (désactive les non-sélectionnés) */
  maxReached?: boolean
  onSelect?: (code: string) => void
}

export default function MetierCard({
  code,
  nom,
  description,
  selected = false,
  selectionOrder,
  maxReached = false,
  onSelect,
  className = '',
  ...props
}: MetierCardProps) {
  const pictoSrc = `/images/metiers/${code.toLowerCase()}.svg`
  const isDisabled = maxReached && !selected

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      aria-disabled={isDisabled}
      onClick={() => !isDisabled && onSelect?.(code)}
      className={cn(
        "group relative w-full flex items-center gap-4 p-4 rounded-xl border text-left",
        "transition-all duration-200 ease-out",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        selected
          ? "bg-accent border-primary shadow-sm"
          : isDisabled
            ? "bg-muted/50 border-border opacity-50 cursor-not-allowed"
            : "bg-card border-border hover:border-primary/50 hover:shadow-sm",
        className
      )}
      {...props}
    >
      {/* Picto à gauche */}
      <img
        src={pictoSrc}
        alt={nom}
        className={cn(
          "flex-shrink-0 w-12 h-12 rounded-lg",
          isDisabled && "grayscale opacity-50",
          !isDisabled && "group-hover:scale-105 transition-transform"
        )}
      />

      {/* Texte au milieu */}
      <div className="flex-1 min-w-0">
        <span className={cn(
          "block text-sm font-semibold leading-tight",
          selected ? "text-foreground" : isDisabled ? "text-muted-foreground" : "text-foreground"
        )}>
          {nom}
        </span>
        {description && (
          <span className={cn(
            "block text-xs mt-0.5 leading-snug",
            isDisabled ? "text-muted-foreground/50" : "text-muted-foreground"
          )}>
            {description}
          </span>
        )}
      </div>

      {/* Indicateur de sélection à droite */}
      <div className={cn(
        "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all",
        selected
          ? "bg-primary text-primary-foreground"
          : "border-2 border-muted-foreground/30"
      )}>
        {selected ? (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        ) : null}
      </div>
    </button>
  )
}
